import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { CheckCircle2, XCircle, Clock, Loader2, ShieldCheck, MessageSquare } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusStyle = (s) => {
  const v = (s || '').toLowerCase();
  if (v === 'accepted') return 'bg-primary text-primary-foreground';
  if (v === 'rejected') return 'bg-red-500/20 text-red-500 border border-red-500/40';
  if (v === 'expired') return 'bg-muted text-muted-foreground';
  return 'bg-yellow-500/20 text-yellow-500 border border-yellow-500/40';
};

const PublicQuote = () => {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null);
  const [showSubscribe, setShowSubscribe] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeForm, setSubscribeForm] = useState({ name: '', email: '', message: '' });
  const [subscribeResult, setSubscribeResult] = useState(null);

  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/public/quotes/${token}`);
        if (ok) setQuote(r.data);
      } catch (err) {
        if (ok) setError(err.response?.status === 404 ? 'Quote link not found' : 'Failed to load quote');
      } finally {
        if (ok) setLoading(false);
      }
    })();
    return () => { ok = false; };
  }, [token]);

  const terminal = quote && ['accepted', 'rejected', 'expired'].includes((quote.status || '').toLowerCase());

  const handleAccept = async () => {
    if (!window.confirm(`Accept this quote for $${quote.total.toFixed(2)}? An invoice will be generated and emailed.`)) return;
    setBusy('accept');
    try {
      const r = await axios.post(`${BACKEND_URL}/api/public/quotes/${token}/accept`);
      setResult(r.data);
      setQuote((q) => ({ ...q, status: 'accepted', accepted_at: new Date().toISOString() }));
      toast.success('Quote accepted. An invoice has been generated.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept quote');
    } finally {
      setBusy(null);
    }
  };

  const handleReject = async () => {
    if (!window.confirm('Reject this quote? This action cannot be undone.')) return;
    setBusy('reject');
    try {
      const r = await axios.post(`${BACKEND_URL}/api/public/quotes/${token}/reject`);
      setResult(r.data);
      setQuote((q) => ({ ...q, status: 'rejected', rejected_at: new Date().toISOString() }));
      toast.success('Quote rejected.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject quote');
    } finally {
      setBusy(null);
    }
  };

  const openSubscribe = () => {
    setSubscribeForm({
      name: '',
      email: quote?.client_name ? '' : '',  // intentionally blank — clients re-confirm
      message: '',
    });
    setSubscribeResult(null);
    setShowSubscribe(true);
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!subscribeForm.email.trim() || !subscribeForm.email.includes('@')) {
      return toast.error('Please enter a valid email');
    }
    if (!subscribeForm.message.trim()) {
      return toast.error('Please add a question or message');
    }
    setSubscribing(true);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/public/quotes/${token}/subscribe-ticket`,
        subscribeForm,
      );
      setSubscribeResult(r.data);
      toast.success('Ticket created — we\'ll be in touch.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open ticket');
    } finally {
      setSubscribing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground font-mono uppercase tracking-widest text-xs">Loading quote…</p>
        </div>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md p-8 text-center border-border" data-testid="public-quote-error">
          <XCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <h1 className="text-2xl font-black mb-2">Link not found</h1>
          <p className="text-muted-foreground">{error || 'The quote you are looking for no longer exists.'}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <div className="text-2xl font-black">
              NEXT<span className="text-primary">/</span>GEN
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mt-1">
              Network Solutions
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="w-4 h-4 text-primary" />
            <span className="hidden sm:inline">Secure quote link</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10" data-testid="public-quote-page">
        {/* Quote header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Quote</p>
            <h1 className="text-3xl sm:text-4xl font-black font-mono">{quote.quote_id}</h1>
            <p className="text-muted-foreground mt-2">Prepared for <span className="text-foreground font-medium">{quote.client_name}</span></p>
          </div>
          <div className="text-right space-y-2">
            <Badge className={`${statusStyle(quote.status)} text-sm`} data-testid="public-quote-status">
              {(quote.status || 'draft').toUpperCase()}
            </Badge>
            {quote.valid_until && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 justify-end">
                <Clock className="w-3 h-3" />
                Valid until {format(new Date(quote.valid_until), 'MMM dd, yyyy')}
              </p>
            )}
          </div>
        </div>

        {/* Line items */}
        <Card className="border-border overflow-hidden mb-6">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="font-mono text-xs uppercase">Description</TableHead>
                <TableHead className="font-mono text-xs uppercase text-right">Qty</TableHead>
                <TableHead className="font-mono text-xs uppercase text-right">Unit Price</TableHead>
                <TableHead className="font-mono text-xs uppercase text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quote.items.map((it, i) => {
                const qty = parseFloat(it.quantity) || 0;
                const up = parseFloat(it.unit_price) || 0;
                return (
                  <TableRow key={i} className="border-border">
                    <TableCell className="align-top">{it.description}</TableCell>
                    <TableCell className="text-right align-top">{qty}</TableCell>
                    <TableCell className="text-right align-top">${up.toFixed(2)}</TableCell>
                    <TableCell className="text-right align-top font-bold">${(qty * up).toFixed(2)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>

        {/* Totals */}
        <Card className="border-border p-6 mb-6">
          <div className="max-w-xs ml-auto space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>${quote.subtotal.toFixed(2)}</span></div>
            {quote.tax_rate > 0 && (
              <div className="flex justify-between"><span className="text-muted-foreground">Tax ({quote.tax_rate}%)</span><span>${quote.tax_amount.toFixed(2)}</span></div>
            )}
            <div className="flex justify-between pt-3 border-t border-border">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Total</span>
              <span className="text-2xl font-black text-primary" data-testid="public-quote-total">${quote.total.toFixed(2)}</span>
            </div>
          </div>
        </Card>

        {/* Notes */}
        {quote.notes && (
          <Card className="border-border p-6 mb-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Notes</p>
            <p className="text-sm whitespace-pre-wrap">{quote.notes}</p>
          </Card>
        )}

        {/* Accept / Reject */}
        {!terminal ? (
          <Card className="border-primary/40 bg-primary/5 p-6" data-testid="public-quote-actions">
            <p className="text-sm mb-4">Ready to proceed? Accepting below will automatically generate an invoice for <strong>${quote.total.toFixed(2)}</strong> and email you a secure link to pay.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                className="flex-1 font-bold uppercase tracking-widest"
                onClick={handleAccept}
                disabled={busy !== null}
                data-testid="public-quote-accept-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {busy === 'accept' ? 'Accepting…' : 'Accept Quote'}
              </Button>
              <Button
                variant="outline"
                className="flex-1 font-bold uppercase tracking-widest"
                onClick={handleReject}
                disabled={busy !== null}
                data-testid="public-quote-reject-btn"
              >
                <XCircle className="w-4 h-4 mr-2" />
                {busy === 'reject' ? 'Rejecting…' : 'Reject'}
              </Button>
            </div>
          </Card>
        ) : (
          <Card className={`p-6 border-2 ${quote.status === 'accepted' ? 'border-primary/60 bg-primary/10' : 'border-border bg-muted/20'}`} data-testid="public-quote-terminal">
            {quote.status === 'accepted' && (
              <>
                <CheckCircle2 className="w-10 h-10 text-primary mb-3" />
                <h2 className="text-2xl font-black mb-2">Quote accepted</h2>
                <p className="text-sm text-muted-foreground">
                  Thanks! {quote.accepted_at && `Accepted on ${format(new Date(quote.accepted_at), 'MMM dd, yyyy · h:mm a')}.`} An invoice has been generated{result?.invoice_id ? ` (${result.invoice_id})` : ''} and will arrive at your email shortly.
                </p>
              </>
            )}
            {quote.status === 'rejected' && (
              <>
                <XCircle className="w-10 h-10 text-red-500 mb-3" />
                <h2 className="text-2xl font-black mb-2">Quote rejected</h2>
                <p className="text-sm text-muted-foreground">
                  {quote.rejected_at && `Rejected on ${format(new Date(quote.rejected_at), 'MMM dd, yyyy · h:mm a')}.`} If this was a mistake, reach out to us and we'll re-issue.
                </p>
              </>
            )}
            {quote.status === 'expired' && (
              <>
                <Clock className="w-10 h-10 text-muted-foreground mb-3" />
                <h2 className="text-2xl font-black mb-2">Quote expired</h2>
                <p className="text-sm text-muted-foreground">
                  The validity window on this quote has passed. Contact us to request a fresh one.
                </p>
              </>
            )}
          </Card>
        )}

        {/* Subscribe to ticket — always available so a visitor can ask a question
            even after accepting/rejecting. */}
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-5 bg-card/40 border border-border rounded-lg" data-testid="public-quote-subscribe-block">
          <div className="flex items-start gap-3">
            <MessageSquare className="w-5 h-5 mt-0.5 text-primary shrink-0" />
            <div>
              <p className="font-bold text-sm">Have a question about this quote?</p>
              <p className="text-xs text-muted-foreground mt-1">
                Open a support ticket and our team will follow up by email.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={openSubscribe}
            className="font-bold uppercase tracking-widest shrink-0"
            data-testid="public-quote-subscribe-btn"
          >
            Subscribe to Ticket
          </Button>
        </div>

        <Dialog open={showSubscribe} onOpenChange={(open) => { setShowSubscribe(open); if (!open) setSubscribeResult(null); }}>
          <DialogContent className="bg-card border-border max-w-lg" data-testid="public-quote-subscribe-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold uppercase">Open a Support Ticket</DialogTitle>
            </DialogHeader>
            {!subscribeResult ? (
              <form onSubmit={handleSubscribe} className="space-y-4">
                <p className="text-xs text-muted-foreground">
                  We'll create a ticket attached to this quote and email you back at the address below.
                </p>
                <div>
                  <Label>Your name (optional)</Label>
                  <Input
                    value={subscribeForm.name}
                    onChange={(e) => setSubscribeForm({ ...subscribeForm, name: e.target.value })}
                    placeholder={quote.client_name}
                    className="bg-input"
                    data-testid="subscribe-name-input"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    required
                    value={subscribeForm.email}
                    onChange={(e) => setSubscribeForm({ ...subscribeForm, email: e.target.value })}
                    placeholder="you@company.com"
                    className="bg-input"
                    data-testid="subscribe-email-input"
                  />
                </div>
                <div>
                  <Label>Your question</Label>
                  <Textarea
                    required
                    rows={5}
                    maxLength={5000}
                    value={subscribeForm.message}
                    onChange={(e) => setSubscribeForm({ ...subscribeForm, message: e.target.value })}
                    placeholder="What would you like clarified before moving forward?"
                    className="bg-input"
                    data-testid="subscribe-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full font-bold uppercase tracking-widest"
                  disabled={subscribing}
                  data-testid="subscribe-submit-btn"
                >
                  {subscribing ? 'Sending…' : 'Open Ticket'}
                </Button>
              </form>
            ) : (
              <div className="space-y-3" data-testid="public-quote-subscribe-success">
                <CheckCircle2 className="w-10 h-10 text-primary" />
                <h3 className="text-2xl font-black">Ticket opened</h3>
                <p className="text-sm text-muted-foreground">{subscribeResult.message}</p>
                <p className="text-xs font-mono text-muted-foreground">
                  Reference: <span className="text-foreground">{subscribeResult.ticket_id}</span>
                </p>
                {subscribeResult.public_url && (
                  <a
                    href={subscribeResult.public_url}
                    className="block text-xs text-primary underline break-all"
                    data-testid="public-ticket-link"
                  >
                    Track status: {subscribeResult.public_url}
                  </a>
                )}
                <Button
                  variant="outline"
                  className="w-full font-bold uppercase tracking-widest"
                  onClick={() => { setShowSubscribe(false); setSubscribeResult(null); }}
                >
                  Close
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <footer className="mt-12 text-center text-xs text-muted-foreground space-y-1">
          <p>Questions? Reply to this email or call <a href="tel:7403025607" className="text-primary">740-302-5607</a>.</p>
          <p>&copy; {new Date().getFullYear()} NextGen Network Solutions</p>
        </footer>
      </main>
    </div>
  );
};

export default PublicQuote;
